import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { B2bOperationalCharge } from '../../../models/Contract';
import { ContractsService } from '../../../services/contracts.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TableAction } from '../../../shared/components/table/table.component';
import { B2bContractPlateOption } from '../b2b-contract-plate-select/b2b-contract-plate-select.component';
import { exportB2bTableToExcel } from '../../../utils/b2b-table-export.util';

@Component({
  selector: 'app-b2b-charges',
  templateUrl: './b2b-charges.component.html',
  styleUrls: ['../shared/b2b-operational-shared.css', './b2b-charges.component.css']
})
export class B2bChargesComponent implements OnInit {
  contractId: number | null = null;
  contractNumber = '';
  contractName = '';
  items: B2bOperationalCharge[] = [];
  isLoading = false;
  submitting = false;
  showModal = false;
  viewMode = false;
  editingId: number | null = null;
  selectedVehicleId: number | null = null;
  filterType: '' | 'Damage' | 'Freight' = '';
  form!: FormGroup;

  readonly typeOptions = [
    { value: 'Damage', label: 'Avaria' },
    { value: 'Freight', label: 'Frete' }
  ];

  readonly statusOptions = [
    { value: 'Pending', label: 'Pendente' },
    { value: 'Billed', label: 'Faturado' },
    { value: 'Paid', label: 'Pago' },
    { value: 'Cancelled', label: 'Cancelado' }
  ];

  columns = [
    { titulo: 'Tipo', label: 'type', formatter: (v: any) => this.typeLabel(v) },
    { titulo: 'Data', label: 'chargeDate', formatter: (v: any) => this.formatDate(v) },
    { titulo: 'Placa', label: 'plate' },
    { titulo: 'Competência', label: 'billingPeriod' },
    { titulo: 'Valor', label: 'amount', formatter: (v: any) => this.formatMoney(v), sensitive: true },
    { titulo: 'Status', label: 'status', formatter: (v: any) => this.statusLabel(v) },
    { titulo: 'Descrição', label: 'description' }
  ];

  actions: TableAction[] = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Editar', icon: 'fas fa-edit', action: 'edit', class: 'btn-edit' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' }
  ];

  get modalTitle(): string {
    if (this.viewMode) return 'Visualizar cobrança';
    return this.editingId ? 'Editar cobrança' : 'Nova cobrança';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private contractsService: ContractsService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      type: ['Damage', Validators.required],
      plate: [''],
      chargeDate: [''],
      billingPeriod: [''],
      amount: [null, [Validators.required, Validators.min(0.01)]],
      description: [''],
      origin: [''],
      destination: [''],
      status: ['Pending', Validators.required],
      notes: ['']
    });

    this.route.params.subscribe(params => {
      this.contractId = +params['id'];
      if (this.contractId) {
        this.loadContractInfo();
        this.loadItems();
      }
    });
  }

  loadContractInfo(): void {
    if (!this.contractId) return;
    this.contractsService.getB2bContractById(this.contractId).subscribe({
      next: (c) => {
        this.contractNumber = c.contractNumber || '';
        this.contractName = c.contractName || '';
      },
      error: (err) => this.errorService.handleHttpError(err)
    });
  }

  loadItems(): void {
    if (!this.contractId) return;
    this.isLoading = true;
    const type = this.filterType || undefined;
    this.contractsService.getB2bCharges(this.contractId, type).subscribe({
      next: (list) => {
        this.items = (list || []).map(x => this.normalize(x));
        this.isLoading = false;
      },
      error: () => { this.isLoading = false; }
    });
  }

  applyFilter(): void {
    this.loadItems();
  }

  openCreate(): void {
    this.viewMode = false;
    this.editingId = null;
    this.selectedVehicleId = null;
    this.form.enable();
    this.form.reset({
      type: this.filterType || 'Damage',
      plate: '',
      chargeDate: '',
      billingPeriod: '',
      amount: null,
      description: '',
      origin: '',
      destination: '',
      status: 'Pending',
      notes: ''
    });
    this.showModal = true;
  }

  openView(item: B2bOperationalCharge): void {
    this.patchForm(item);
    this.viewMode = true;
    this.form.disable();
    this.showModal = true;
  }

  openEdit(item: B2bOperationalCharge): void {
    this.patchForm(item);
    this.viewMode = false;
    this.form.enable();
    this.showModal = true;
  }

  private patchForm(item: B2bOperationalCharge): void {
    this.editingId = item.id ?? null;
    this.selectedVehicleId = item.vehicleId ?? null;
    this.form.patchValue({
      type: item.type || 'Damage',
      plate: item.plate || '',
      chargeDate: this.toDateInput(item.chargeDate),
      billingPeriod: item.billingPeriod || '',
      amount: item.amount ?? null,
      description: item.description || '',
      origin: item.origin || '',
      destination: item.destination || '',
      status: item.status || 'Pending',
      notes: item.notes || ''
    });
  }

  onPlateSelected(option: B2bContractPlateOption | null): void {
    this.selectedVehicleId = option?.vehicleId ?? null;
  }

  closeModal(): void {
    this.showModal = false;
    this.viewMode = false;
    this.editingId = null;
    this.selectedVehicleId = null;
    this.form.enable();
  }

  submit(): void {
    if (this.viewMode || !this.contractId || this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const raw = this.form.getRawValue();
    const payload: B2bOperationalCharge = {
      b2bContractId: this.contractId,
      type: raw.type,
      vehicleId: this.selectedVehicleId,
      plate: raw.plate || null,
      chargeDate: raw.chargeDate ? new Date(raw.chargeDate).toISOString() : null,
      billingPeriod: raw.billingPeriod || null,
      amount: Number(raw.amount),
      description: raw.description || null,
      origin: raw.origin || null,
      destination: raw.destination || null,
      status: raw.status || 'Pending',
      notes: raw.notes || null
    };

    this.submitting = true;
    const req$ = this.editingId
      ? this.contractsService.updateB2bCharge(this.contractId, this.editingId, { ...payload, id: this.editingId })
      : this.contractsService.createB2bCharge(this.contractId, payload);

    req$.subscribe({
      next: () => {
        this.submitting = false;
        this.errorService.showSuccessMessage(this.editingId ? 'Cobrança atualizada.' : 'Cobrança criada.');
        this.closeModal();
        this.loadItems();
      },
      error: () => { this.submitting = false; }
    });
  }

  onActionClick(event: { action: string; item: B2bOperationalCharge }): void {
    if (event.action === 'view') this.openView(event.item);
    if (event.action === 'edit') this.openEdit(event.item);
    if (event.action === 'delete') this.deleteItem(event.item);
  }

  deleteItem(item: B2bOperationalCharge): void {
    if (!this.contractId || !item.id) return;
    this.confirmationService.confirmDelete(item.plate || item.description || 'esta cobrança', 'a cobrança').subscribe({
      next: (ok) => {
        if (!ok) return;
        this.contractsService.deleteB2bCharge(this.contractId!, item.id!).subscribe({
          next: () => {
            this.errorService.showSuccessMessage('Cobrança excluída.');
            this.loadItems();
          }
        });
      }
    });
  }


  exportExcel(): void {
    exportB2bTableToExcel(
      `b2b-cobrancas-${this.contractNumber || this.contractId}`,
      this.columns,
      this.items,
      'Cobrancas'
    );
  }

  navigateBack(): void {
    if (this.contractId) this.router.navigate(['/b2b/contracts', this.contractId, 'view']);
    else this.router.navigate(['/b2b/contracts']);
  }

  private normalize(dto: any): B2bOperationalCharge {
    return {
      id: dto.id ?? dto.Id,
      b2bContractId: dto.b2bContractId ?? dto.b2BContractId ?? dto.B2BContractId ?? this.contractId!,
      type: dto.type ?? dto.Type ?? 'Damage',
      vehicleId: dto.vehicleId ?? dto.VehicleId ?? null,
      plate: dto.plate ?? dto.Plate ?? null,
      chargeDate: dto.chargeDate ?? dto.ChargeDate ?? null,
      billingPeriod: dto.billingPeriod ?? dto.BillingPeriod ?? null,
      amount: Number(dto.amount ?? dto.Amount ?? 0),
      description: dto.description ?? dto.Description ?? null,
      origin: dto.origin ?? dto.Origin ?? null,
      destination: dto.destination ?? dto.Destination ?? null,
      status: dto.status ?? dto.Status ?? 'Pending',
      accountsReceivableId: dto.accountsReceivableId ?? dto.AccountsReceivableId ?? null,
      notes: dto.notes ?? dto.Notes ?? null
    };
  }

  private toDateInput(value: any): string {
    if (!value) return '';
    const d = new Date(value);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
  }

  formatDate(value: any): string {
    if (!value) return '—';
    try { return new Date(value).toLocaleDateString('pt-BR'); } catch { return String(value); }
  }

  formatMoney(value: any): string {
    if (value == null || value === '') return '—';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  typeLabel(value: string): string {
    return this.typeOptions.find(t => t.value === value)?.label || value || '—';
  }

  statusLabel(value: string): string {
    return this.statusOptions.find(s => s.value === value)?.label || value || '—';
  }
}
